import { ChangeDetectorRef, Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { BaseComponent, ResponseService, WindowRef } from '@portals/core';
import { ChannelsState, NotificationsState } from '@portals/store';
import { AWSUploadService, Channel, CollectibleService, LINK_TYPES, LinkService, Notification } from '@portals/api';
import { map, mergeMap, tap, finalize } from 'rxjs/operators';
import { NgxQrcodeElementTypes, NgxQrcodeErrorCorrectionLevels } from '@techiediaries/ngx-qrcode';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@portals/environments';
import { SCREENS } from '@portals/utils';
import mqtt, { MqttClient } from 'mqtt';

@Component({
	selector: 'portals-leave-message',
	templateUrl: './leave-message.component.html',
	styleUrls: ['./leave-message.component.scss'],
})
export class LeaveMessageComponent extends BaseComponent implements OnInit {
	protected readonly LINK_TYPES = LINK_TYPES;
	protected readonly SCREENS = SCREENS;

	url!: string;
	channel!: Channel;
	feed!: Notification[];
	notification!: Notification;
	mqttClient?: MqttClient;
	channelUUID: string;
	notificationID = environment.defaultNotification as number;
	isMobile = false;
	showScrollToNextPageBtn = false;
	elementType = NgxQrcodeElementTypes.URL;
	correctionLevel = NgxQrcodeErrorCorrectionLevels.HIGH;
	grayPortionHeight = 0;
	qrCodeColor = {
		dark: '#491176',
		light: '#fbd354',
	};
	NgxQrcodeElementTypes = {
		URL: 'url',
		IMG: 'img',
		CANVAS: 'canvas',
		SVG: 'svg',
	};
	ready = false;
	wordCount: { counter: number; words: any[]; autorefresh: boolean } = { counter: 0, words: [], autorefresh: true };
	isSTV = false;
	currentLanguage = 'en';

	constructor(private channelState: ChannelsState, private notificationState: NotificationsState, private translate: TranslateService, public deviceService: DeviceDetectorService, public window: WindowRef, private awsUpload: AWSUploadService, private collectibleService: CollectibleService, public cdr: ChangeDetectorRef, public linkService: LinkService, private router: Router, private http: HttpClient, private route: ActivatedRoute, public responseService: ResponseService) {
		super();

		const systemDefaultLanguage = navigator.language.split('-')[0];
		const supportedLanguages = ['en', 'es', 'ar'];
		const defaultLanguage = supportedLanguages.includes(systemDefaultLanguage) ? systemDefaultLanguage : 'en';
		translate.setDefaultLang(defaultLanguage);
		this.currentLanguage = 'ar';

		this.channelUUID = this.router.url.split('/').length > 2 ? this.router.url.split('/').pop()! : (environment.defaultChannel as string);
		this.route.queryParams.subscribe((params) => {
			if (params['id']) {
				this.notificationID = params['id'];
				this.isSTV = true;
			}

			this.notificationState
				.loadItem(this.notificationID)
				.pipe(
					map((data: Notification) => {
						this.notification = data;
					}),
					mergeMap(() => this.channelState.findOnebyUUID(this.channelUUID)),
					tap((data: Channel) => {
						this.channel = data;
						this.url = this.isSTV ? (this.notification.alt_stv_url as string) : this.channel.share_url;
						this.responseService.currentPage = {
							screen: SCREENS.WELCOME_SCREEN,
							parameters: {
								channel: data,
								notification: this.notification,
							},
						};
						this.cdr.detectChanges();
					}),
					finalize(() => {
						this.ready = true;
					})
				)
				.subscribe();
		});

		this.isMobile = deviceService.isMobile();
	}

	observeTopic(topic: string) {
		if (this.mqttClient) {
			this.mqttClient.subscribe(topic, (err) => {
				if (err) {
					console.error('Error subscribing to MQTT topic:', err);
				} else {
					console.log('Subscribed to MQTT topic:', topic);
				}
			});
		}
	}

	fetchData() {
		this.collectibleService
			.momentQuery(this.notificationID)
			.pipe(
				tap((data) => {
					this.wordCount.words = data.slice(0, 40);
				})
			)
			.subscribe();
	}

	@HostListener('window:scroll', [])
	onWindowScroll() {
		const scrollToTopBtn = document.getElementById('jottoWelcome');

		if (scrollToTopBtn) {
			if (document.body.scrollTop > 20 || document.documentElement.scrollTop > 20) {
				scrollToTopBtn.style.display = 'block';
			} else {
				// scrollToTopBtn.style.display = 'none';
			}
		}
	}

	ngOnInit(): void {
		this.fetchData();

		this.awsUpload.uploadComplete$.subscribe((completed) => {
			if (completed) {
				if (this.notification.external_url && this.notification.shouldRedirect) {
					if (this.deviceService.isDesktop()) {
						const newTab = window.open(this.notification.external_url, '_blank');
						newTab?.focus();
					} else {
						window.open(this.notification.external_url, '_self');
					}
				}
				this.responseService.navigateTo(SCREENS.CHANNEL_FEED_SCREEN, {
					channel: this.channel,
					notification: this.notification,
				});
			}
			this.cdr.detectChanges();
		});

		const grayPortion = document.getElementById('grayPortion');
		this.grayPortionHeight = grayPortion?.offsetHeight || 0;
		document.addEventListener('scroll', () => {
			const firstPageHeight = 500;
			const scrollPosition = window.scrollY || document.documentElement.scrollTop;
			this.showScrollToNextPageBtn = scrollPosition > firstPageHeight;
		});

		try {
			this.mqttClient = mqtt.connect('wss://' + 'message.jotto.me');

			this.mqttClient.on('connect', () => {
				this.observeTopic(`/clouds/${this.notificationID}`);
			});

			this.mqttClient.on('message', (topic, message) => {
				const data = JSON.parse(message.toString());
				const words = data.words.slice(0, 40);
				const updatedWordCount = [...this.wordCount.words];

				words.forEach((word: string) => {
					const index = updatedWordCount.findIndex((item) => item.name === word);
					if (index >= 0) {
						updatedWordCount[index].count++;
					} else {
						updatedWordCount.push({ id: updatedWordCount.length + 1, name: word, count: 1 });
					}
				});

				this.wordCount = { counter: this.wordCount.counter++, words: updatedWordCount, autorefresh: true };
			});
		} catch (err) {
			console.error('Error connecting to MQTT:', err);
		}
	}

	scrollToTop() {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}

	scrollDiv() {
		this.scrollToTop();
	}

	navigateToJottoSite() {
		window.open('https://www.jotto.video', '_blank');
	}
}
